import { ExternalLink, Pencil, Smartphone } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/utils/format";
import type { AppVersion } from "@/api/appVersions.api";
import { APP_LABEL } from "@/features/appVersions/types";

export function PolicyCard({
  policy,
  canEdit,
  onEdit,
}: {
  policy: AppVersion;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const isSet = policy.latestBuildNumber !== null;
  const stores = [
    { label: "Google Play", url: policy.androidStoreUrl },
    { label: "App Store", url: policy.iosStoreUrl },
  ];

  return (
    <div className="card flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
            <Smartphone className="h-5 w-5" />
          </span>
          <div>
            <p className="font-semibold text-ink">{APP_LABEL[policy.app]}</p>
            <p className="text-xs text-ink-faint">
              {!isSet
                ? "No version set yet — users aren't asked to update"
                : policy.forceUpdate
                  ? "Users on an older version are blocked until they update"
                  : "Users on an older version are prompted to update"}
            </p>
          </div>
        </div>
        <Badge tone={policy.forceUpdate ? "danger" : "neutral"}>
          {!isSet ? "Not set" : policy.forceUpdate ? "Forced update" : "Optional update"}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_1.4fr]">
        <div className="rounded-lg border border-border bg-cream-100 p-3">
          <p className="text-xs text-ink-faint">Latest version (Android & iOS)</p>
          <p className="font-mono text-2xl font-semibold text-ink">{policy.latestBuildNumber ?? "—"}</p>
        </div>
        <div className="flex flex-col justify-center gap-1.5 rounded-lg border border-border bg-cream-100 p-3 text-sm">
          {stores.map((s) => (
            <div key={s.label} className="flex items-center justify-between gap-2">
              <span className="text-ink-faint">{s.label}</span>
              {s.url ? (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-teal-600 hover:underline"
                >
                  Open link <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-ink-faint">Not set</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-ink-faint">Behaviour</dt>
        <dd className="text-right text-ink">
          {policy.forceUpdate ? "Full-screen, can't be dismissed" : "Dismissible prompt"}
        </dd>
        <dt className="text-ink-faint">Release notes</dt>
        <dd className="truncate text-right text-ink">{policy.releaseNotes || "—"}</dd>
      </dl>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
        <p className="truncate text-xs text-ink-faint">
          {policy.updatedAt
            ? `Updated ${formatDateTime(policy.updatedAt)}${policy.updatedByName ? ` by ${policy.updatedByName}` : ""}`
            : "Never updated"}
        </p>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" /> Edit policy
          </Button>
        )}
      </div>
    </div>
  );
}
